import { useState } from "react";
import SectionLabel from "@/components/SectionLabel";
import { ChevronDown } from "lucide-react";

const personnel = [
  { role: "Parish Priest", name: "Rev. Fr. Ariel D. Destora" },
  { role: "Secretary", name: "Aileen Julagting" },
  { role: "Cashier", name: "Celestina Feguro" },
  { role: "Maintenance & Utility", name: "Joey Llander" },
];

const councils = [
  {
    name: "Parish Pastoral Council (PPC)",
    rows: [
      ["President", "Elden Esteban"],
      ["Vice-President", "Kevin Paul Palaca"],
      ["Secretary", "Jason Ralph Fortin"],
    ],
  },
  {
    name: "Parish Economic Council (PEC)",
    rows: [
      ["President", "Engr. Roger Tomenio"],
      ["Vice-President", "Rene Lariba"],
      ["Secretary", "Lilia Carbon"],
      ["Treasurer", "Reynan Dacera"],
      ["Auditor", "Sr. Marynor Cabral, DST"],
    ],
    members: [
      "Engr. Van Christian Alfanta",
      "Celestina Feguro",
      "Rolando Tomenio",
    ],
  },
];

const ministries = [
  {
    name: "Gagmay'ng Kristohanong Katilingban (GKK)",
    rows: [
      ["President", "Elden Esteban"],
      ["Vice President", "Blystar Cydric Josol"],
      ["Secretary", "Jacint Ann Batusin"],
      ["Treasurer", "Felsie Kimmy Jacadino"],
      ["Auditor", "Cremlin Mangrobang"],
    ],
  },
  {
    name: "Catholic Youth Ministry (CYM)",
    rows: [
      ["President", "Jason Ralph Fortin"],
      ["Vice President", "Keisha Quijardo"],
      ["Secretary", "Avril Rose Alegre"],
      ["Treasurer", "Francine Shane Ganohay"],
      ["Auditor", "Elaine Arcuyo"],
    ],
  },
  {
    name: "Family and Life Ministry (FLM)",
    rows: [
      ["President", "Mary Grace Eliver"],
      ["Vice President", "Cherry Labesores"],
      ["Secretary", "Amie Orcia"],
      ["Treasurer", "Gloria Mobera"],
      ["Auditor", "Rosemarie Tignawan"],
    ],
  },
  {
    name: "Catechetical Ministry",
    rows: [
      ["President", "Lelia Carbon"],
      ["Vice President", "Maria Annabelle Perjes"],
      ["Secretary", "Ana Emma Montesclaros"],
      ["Treasurer", "Judith Ramos"],
      ["Auditor", "Jolita Sabanal"],
    ],
  },
  {
    name: "Safeguarding Ministry",
    rows: [
      ["Adult Guide", "Mary Grace Eliver"],
    ],
    members: [
      "Jason Ralph Fortin · Youth Advocate",
      "Brian Mathew Jayson · Youth Advocate",
      "Christine Mae Sabulao · Youth Advocate",
    ],
  },
  {
    name: "Lay Vocation Promoters (LVP)",
    rows: [
      ["President", "Chryslene Diane Valles"],
      ["Vice President", "Liezel Torrejos"],
      ["Secretary", "Jessica Marabi"],
      ["Treasurer", "Ritchell Rivera"],
      ["Auditor", "Jessica Enghog"],
    ],
  },
  {
    name: "Lay Liturgical Leadership Ministry (LLLM)",
    rows: [
      ["President", "Roger Tomenio"],
      ["Vice President", "Nathaniel Macailing"],
      ["Secretary", "Teresa Bajao"],
      ["Treasurer", "Bong Dorato"],
      ["Auditor", "Willie Solatorio"],
    ],
  },
  {
    name: "Lay Liturgical Ministry (LLM)",
    rows: [
      ["President", "Rose Marie Lariba"],
      ["Vice President", "Nancy Garbo"],
      ["Secretary", "Roxanne Mamalias"],
      ["Treasurer", "Mary Jane Banga"],
      ["Auditor", "Arnel Fortin"],
      ["Sacred Music", "Marilyn Liquit"],
      ["Sacred Arts", "Roxanne Mamalias"],
      ["Sacred Rites", "Mae Ann Sumalinog"],
    ],
  },
  {
    name: "Lectors & Commentators",
    rows: [["Coordinator", "Mae Ann Sumalinog"]],
  },
  {
    name: "Altar Servers' Ministry",
    rows: [
      ["President", "Zharyna Tupas"],
      ["Vice President", "Orly Wata"],
      ["Secretary", "Jade Mark Morales"],
      ["Treasurer", "Justine Arsolon"],
      ["Auditor", "Angel Cajegas"],
      ["Coordinator", "Mr. Ray Ken Garzon"],
    ],
  },
  {
    name: "Mother Butler Guild",
    rows: [
      ["President", "Jocelyn Tomenio"],
      ["Vice President", "Mary Jean Cadiz"],
      ["Secretary", "Jane Esteban"],
      ["Treasurer", "Rosalia Tomenio"],
      ["Auditor", "Evelyn Wata"],
    ],
  },
];

export default function Personnel() {
  const [open, setOpen] = useState(null);

  return (
    <section id="personnel" className="py-24 md:py-36 bg-parchment-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel
          kicker="Servant Leaders"
          title="Parish Personnel & Officers"
          intro="The Parish Priest, parish staff, councils, and ministry officers who serve our community in worship, formation, and mission."
        />

        {/* Personnel + Councils */}
        <div className="mt-16 grid lg:grid-cols-12 gap-px bg-parchment-400">
          <div className="lg:col-span-5 bg-parchment-50 p-8 md:p-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-terracotta mb-3 font-sans">
              Parish Personnel
            </div>
            <h3 className="font-serif text-3xl text-cocoa-900 mb-8">Parish Office</h3>
            <ul className="space-y-5">
              {personnel.map((p) => (
                <li key={p.name} className="border-b border-parchment-400 pb-4 last:border-0">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-sage-dark mb-1 font-sans">
                    {p.role}
                  </div>
                  <div className="font-serif text-xl text-cocoa-900">{p.name}</div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7 bg-parchment-100 p-8 md:p-10 space-y-10">
            {councils.map((c) => (
              <div key={c.name}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-sage-dark mb-3 font-sans">
                  Council
                </div>
                <h3 className="font-serif text-2xl text-cocoa-900 mb-5">{c.name}</h3>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {c.rows.map(([role, name]) => (
                    <li
                      key={role + name}
                      className="flex items-baseline gap-3 border-b border-parchment-300 py-2"
                    >
                      <span className="text-[10px] uppercase tracking-[0.22em] text-terracotta w-28 flex-shrink-0">
                        {role}
                      </span>
                      <span className="font-sans text-cocoa-900 text-sm">{name}</span>
                    </li>
                  ))}
                </ul>
                {c.members && (
                  <div className="mt-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mb-2 font-sans">
                      Members
                    </div>
                    <ul className="font-sans text-cocoa-800 text-sm space-y-1">
                      {c.members.map((m) => (
                        <li key={m}>· {m}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Ministries — accordion */}
        <div className="mt-16">
          <div className="text-[10px] uppercase tracking-[0.3em] text-sage-dark mb-3 font-sans text-center">
            Ministry Officers
          </div>
          <h3 className="font-serif text-3xl md:text-4xl text-cocoa-900 text-center mb-10">
            Ministries of the Parish
          </h3>

          <div className="bg-parchment-50 border border-parchment-400 divide-y divide-parchment-400">
            {ministries.map((m, idx) => {
              const isOpen = open === idx;
              return (
                <div key={m.name}>
                  <button
                    onClick={() => setOpen(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 px-6 md:px-8 py-5 text-left hover:bg-parchment-100 transition-colors"
                  >
                    <span className="font-serif text-xl md:text-2xl text-cocoa-900">
                      {m.name}
                    </span>
                    <ChevronDown
                      size={18}
                      className={`text-terracotta flex-shrink-0 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-6 md:px-8 pb-8 bg-parchment-100/50">
                      <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3 pt-2">
                        {m.rows.map(([role, name]) => (
                          <li
                            key={role + name}
                            className="flex items-baseline gap-4 border-b border-parchment-300 py-2"
                          >
                            <span className="text-[10px] uppercase tracking-[0.22em] text-terracotta w-32 flex-shrink-0">
                              {role}
                            </span>
                            <span className="font-sans text-cocoa-900 text-sm">{name}</span>
                          </li>
                        ))}
                      </ul>
                      {m.members && (
                        <div className="mt-5">
                          <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mb-2 font-sans">
                            Members
                          </div>
                          <ul className="font-sans text-cocoa-800 text-sm space-y-1">
                            {m.members.map((mem) => (
                              <li key={mem}>· {mem}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-8 text-center font-sans text-sm text-cocoa-700 italic max-w-2xl mx-auto">
            ✦ As of June 23, 2026, the following are awaiting officers: Kriska Ministry,
            ROMs, Pool of Readers, and Choir.
          </p>
        </div>
      </div>
    </section>
  );
}
